import React, { useEffect, useState } from "react";
import { FaPlus } from "react-icons/fa";
import { MdOpenInNew } from "react-icons/md";
import { Avatar, Checkbox, LinearProgress, Tooltip } from "@mui/material";
import styles from "./table.module.css";
import MenuItem from "@mui/material/MenuItem";
import Menu from "@mui/material/Menu";
import Button from "@mui/material/Button";
import { IoAdd, IoEllipsisVertical } from "react-icons/io5";
import {
  changeTaskState,
  deleteSubTask,
  deleteTask,
  getTasks,
} from "../../api/Api";
import AddUpdateTask from "../modals/AddUpdateTask";
import AddUpdateSubTask from "../modals/AddUpdateSubTask";
import { showToast } from "../../utils/toastUtils";
import Drawer from "@mui/material/Drawer";
import TaskComments from "../TaskComments";
import { BiSolidBookAdd } from "react-icons/bi";
import { BsMagic } from "react-icons/bs";
import AddNewLesson from "../modals/AddNewLesson";
import ShowGeneratedTasks from "../modals/ShowGeneratedTasks";
import { IoSettingsOutline } from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import Box from "../../components/ui/Box";
import { Header } from "../../pages/Home";
import CustomButton from "../CustomButton";
import { stringToColor } from "../../utils/colorUtils";
import AutoCompleteDropdown from "../Inputs/AutoCompleteDropdown";

// styles for the checkbox
const checkBoxStyles = {
  color: "#333333",
  "&.Mui-checked": {
    color: "var(--blue)",
  },
};

const priorities = [{ value: "High" }, { value: "Medium" }, { value: "Low" }];

// Helper function to format date
const formatDate = (dateString) => {
  if (!dateString) return "-";

  const date = new Date(dateString);

  const options = { month: "short", day: "numeric" };
  return date.toLocaleDateString("en-US", options);
};

const TaskTable = ({
  projectId,
  workStreamId,
  members,
  context = "project",
}) => {
  const [tasks, setTasks] = useState([]);
  const [openTaskCreateUpdate, setOpenTaskCreateUpdate] = useState(false);
  const [openSubTaskCreateUpdate, setOpenSubTaskCreateUpdate] = useState(false);
  const [openGeneratedTasksPopup, setOpenGeneratedTasksPopup] = useState({
    show: false,
    goal: {},
  }); // state for showing generated tasks popup
  const [openComments, setOpenComments] = useState(false);
  const [taskPopupOptions, setTaskPopupOptions] = useState({
    is_new: true,
    task: {},
    subTask: {},
  });
  const [commentSectionOptions, setCommentSectionOptions] = useState({
    is_sub: false,
    id: 0,
    title: "",
  });

  const [selectPriority, setSelectPriority] = useState("");
  const [selectedAssigneeId, setSelectedAssigneeId] = useState("");
  const [assignees, setAssignees] = useState([]);
  const headerTitle =
    context === "workstream" ? "WorkStream Goals" : "Project Goals"; // title for the goals header
  const handleAssigneeSelect = (event, value) => {
    value ? setSelectedAssigneeId(value.id) : setSelectedAssigneeId("");
  };

  const handlePrioritySelect = (event, value) => {
    value ? setSelectPriority(value.value) : setSelectPriority("");
  };
  const navigate = useNavigate();
  useEffect(() => {
    setAssignees(members);
  }, []);

  useEffect(() => {
    loadAllProjectTasks();
  }, [selectedAssigneeId, selectPriority, workStreamId]);

  const loadAllProjectTasks = () => {
    getTasks({
      project_id: workStreamId ? null : projectId,
      workStream_id: workStreamId,
      priority: selectPriority,
      assignee: selectedAssigneeId,
    }).then((res) => {
      setTasks(res.data);
    });
  };

  const setTaskPopupOption = (updatedOptions) => {
    setTaskPopupOptions((prevOptions) => ({
      ...prevOptions,
      ...updatedOptions,
    }));
  };

  // close the generated tasks popup
  const closeGeneratedTasksPopup = () => {
    setOpenGeneratedTasksPopup({ show: false, goal: {} });
  };

  // generate task
  const openGenerateTasksPopup = async (goal) => {
    console.log(goal);
    await setOpenGeneratedTasksPopup({ show: true, goal: goal });
  };

  // open the add/update task popup
  const openAddUpdateTaskPopup = async (is_new = true, task) => {
    await setTaskPopupOption({ is_new, task: task });
    setOpenTaskCreateUpdate(true);
  };

  // open the add/update sub task popup
  const openAddUpdateSubTaskPopup = async (is_new = true, task, subTask) => {
    console.log(task);
    await setTaskPopupOption({ is_new, task: task, subTask: subTask });
    setOpenSubTaskCreateUpdate(true);
  };

  const toggleCommentSection = (is_sub = false, id = 0, title = "") => {
    console.log(title);
    console.log(id);
    setCommentSectionOptions((prevOptions) => ({
      ...prevOptions,
      id: id,
      is_sub: is_sub,
      title: title,
    }));
    setOpenComments(!openComments);
  };

  const checkTaskAvailableToMark = (row) => {
    for (let item of row.subtasks) {
      if (!item.is_completed) {
        return {
          disabled: true,
        };
      }
    }

    return {
      disabled: false,
    };
  };

  const markAsDone = (item, isMainTask) => {
    const obj = {
      id: item.id,
      completed: !item.is_completed,
    };

    changeTaskState(obj).then((res) => {
      if (res.status === 200) {
        const statusMessage = obj.completed ? "done" : "in progress";
        const name = isMainTask ? "Goal" : "Task";
        showToast(`${name} was marked as ${statusMessage}`, "success");
        console.log(name);
        loadAllProjectTasks();
      }
    });
  };

  const getProgress = (progress, is_completed) => {
    const [numerator, denominator] = progress.split("/").map(Number);

    if (denominator === 0 && is_completed) {
      return 100;
    }

    if (denominator === 0) {
      return 0;
    }

    return (numerator / denominator) * 100;
  };

  const setSubtask = (task) => {
    openAddUpdateSubTaskPopup(true, task, {});
  };

  const handleClick = (project_id) => {
    navigate(`/project/goals/settings/${project_id}`);
  };

  // actions buttons for the header
  const actions = (
    <div className="d-flex gap-2">
      <CustomButton onClick={openAddUpdateTaskPopup}>
        <IoAdd color="#fff" /> New Goal
      </CustomButton>
      <div
        onClick={() => handleClick(projectId)}
        style={{ backgroundColor: "#0ba7ff20", cursor: "pointer" }}
        className="d-flex align-items-center rounded-4  p-3 "
      >
        <IoSettingsOutline color="#0ba7ffdb" size={20} />
      </div>
    </div>
  );

  return (
    <div className="">
      <Box innerClass="primary-box">
        <Header title={headerTitle} showButton={false} actions={actions} />
        <div className="d-flex flex-column flex-md-row column-gap-3 p-3 p-xl-4">
          <div className="col-lg-4 col-md-5">
            <AutoCompleteDropdown
              label="Assignee"
              options={assignees}
              onChange={handleAssigneeSelect}
              getOptionLabel={(option) => option?.name || ""}
              placeholder="Select Assignee"
            />
          </div>
          <div className="col-lg-4 col-md-5">
            <AutoCompleteDropdown
              label="Priority"
              options={priorities}
              onChange={handlePrioritySelect}
              getOptionLabel={(option) => option?.value || "x"}
              placeholder="Select Priority"
            />
          </div>
        </div>
        <Box
          innerClass="primary-box overflow-x-scroll"
          className={`${styles.tableWrapper} ${styles.styledScroll}`}
        >
          <div className={styles.tableContainer}>
            {/* Table Header */}
            <div className={styles.tableHeader}>
              <div className={`${styles.headerCell} ${styles.task}`}>Title</div>
              <div className={`${styles.headerCell} ${styles.assignee}`}>
                Assignee
              </div>
              <div className={`${styles.headerCell} ${styles.priority}`}>
                Priority
              </div>
              <div className={`${styles.headerCell} ${styles.progress}`}>
                Progress
              </div>
              <div className={`${styles.headerCell} ${styles.comments}`}>
                Comments
              </div>
              <div className={`${styles.headerCell} ${styles.dueDate}`}>
                Due Date
              </div>
              <div className={`${styles.headerCell} ${styles.actions}`}>
                Actions
              </div>
            </div>

            {/* Empty Rows */}
            {tasks.length === 0 && (
              <div className="text-center p-4 p4">
                <span>
                  No goals found. To add a new goal, click on the "New Goal"
                  button.
                </span>
              </div>
            )}

            {/* Table Rows */}
            {tasks?.map((row) => (
              <div className="my-2" style={{ cursor: "pointer" }} key={row.id}>
                <div className={styles.tableRow}>
                  {/* checkbox */}
                  <div
                    className={`${styles.cell} ${styles.task}`}
                    onClick={() => openAddUpdateTaskPopup(false, row)}
                  >
                    <Checkbox
                      {...checkTaskAvailableToMark(row)}
                      type="checkbox"
                      checked={row.is_completed}
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation(); // Prevents parent onClick from triggering
                        markAsDone(row, true);
                      }}
                      sx={checkBoxStyles}
                    />

                    <span className={`ms-3 ${styles.truncateText}`}>
                      {row.title}
                    </span>
                  </div>

                  {/* goal assignees */}
                  <div className={`${styles.cell} ${styles.assignee}`}>
                    {/* Show up to 3 avatars, with the third showing the excess count if needed */}
                    <div className="position-relative">
                      {row.assignees.slice(0, 3).map((assignee, index) => (
                        <Tooltip
                          key={index}
                          title={`${assignee.first_name} ${assignee.last_name}`}
                          arrow
                          placement="top"
                        >
                          <Avatar
                            alt={`${assignee.first_name} ${assignee.last_name}`}
                            src={" "}
                            sx={{
                              position: "absolute",
                              left: index * 15, // Adjust positioning for overlapping
                              fontSize: 14,
                              width: 30,
                              height: 30,
                              bgcolor: assignee.profile_picture
                                ? "transparent"
                                : stringToColor(assignee.first_name),
                            }}
                          />
                        </Tooltip>
                      ))}
                      {row.assignees.length > 3 && (
                        <span className={styles.assigneeCount}>
                          +{row.assignees.length - 3}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* goal priorities */}
                  <div className={`${styles.cell} ${styles.priority}`}>
                    {row.priority ? row.priority : "-"}
                  </div>

                  {/* goal progress */}
                  <div
                    className={`${styles.cell} ${styles.progress}`}
                    style={{ padding: "0.5rem 0" }}
                  >
                    <LinearProgress
                      variant="determinate"
                      value={getProgress(row.progress, row.is_completed)}
                      sx={{
                        height: 13,
                        borderRadius: 10,
                        width: "70%", // Reduce width slightly for better fit within the cell
                        backgroundColor:
                          "color-mix(in srgb, var(--blue) 10%, transparent 90%)",
                        "& .MuiLinearProgress-bar": {
                          backgroundColor: "var(--blue)",
                        },
                      }}
                    />
                  </div>

                  {/* goal comments */}
                  <div
                    className={`${styles.cell} ${styles.comments}`}
                    onClick={() =>
                      toggleCommentSection(false, row.id, row.title)
                    }
                  >
                    <div className={styles.iconLink}>
                      {row.commentsCount}{" "}
                      <MdOpenInNew
                        style={{
                          color: "rgba(56, 56, 56, 0.4)",
                          fontSize: "15",
                        }}
                      />
                    </div>
                  </div>

                  {/* goal due date */}
                  <div className={`${styles.cell} ${styles.dueDate}`}>
                    {formatDate(row.due_date)}
                  </div>

                  {/* goal actions */}
                  <div className={`${styles.cell} ${styles.actions}`}>
                    <div className={styles.actionIcons}>
                      {/* use plusButton style for consistency */}
                      <Tooltip title="Generate tasks" arrow placement="top">
                        <span>
                          <BsMagic
                            className={styles.plusButton}
                            onClick={() => {
                              openGenerateTasksPopup(row);
                            }}
                            size={13}
                          />
                        </span>
                      </Tooltip>
                      <Tooltip title="Add a task" arrow placement="top">
                        <button
                          onClick={() => openAddUpdateSubTaskPopup(true, row)}
                          className={styles.plusButton}
                        >
                          <FaPlus size={13} />
                        </button>
                      </Tooltip>

                      {row.is_completed && (
                        <Tooltip
                          title="Add a lesson learned."
                          arrow
                          placement="top"
                        >
                          <span>
                            <AddNewLessonIcon taskId={row?.id} />
                          </span>
                        </Tooltip>
                      )}
                      <ActionDropdown
                        is_sub_task={false}
                        taskId={row.id}
                        loadAll={loadAllProjectTasks}
                      />
                    </div>
                  </div>
                </div>

                {/* tasks */}
                {row.subtasks.map((subRow) => (
                  <div key={subRow.id} className={styles.tableRow}>
                    <div
                      className={`${styles.cell} ${styles.task} ${styles.subRow}`}
                      onClick={() =>
                        openAddUpdateSubTaskPopup(false, row, subRow)
                      }
                    >
                      <Checkbox
                        type="checkbox"
                        size="small"
                        checked={subRow.is_completed}
                        onClick={(e) => {
                          e.stopPropagation(); // Prevents parent onClick from triggering
                          markAsDone(subRow, false);
                        }}
                        sx={checkBoxStyles}
                      />

                      <span className={`ms-3 ${styles.truncateText}`}>
                        {subRow.title}
                      </span>
                    </div>

                    {/* subtask assignees */}
                    <div className={`${styles.cell} ${styles.assignee}`}>
                      {/* Show up to 3 avatars, with the third showing the excess count if needed */}
                      <div className="position-relative">
                        {subRow.assignees.slice(0, 3).map((assignee, index) => (
                          <Tooltip
                            key={index}
                            title={`${assignee.first_name} ${assignee.last_name}`}
                            arrow
                            placement="top"
                          >
                            <Avatar
                              alt={`${assignee.first_name} ${assignee.last_name}`}
                              src={" "}
                              sx={{
                                position: "absolute",
                                left: index * 15, // Adjust positioning for overlapping
                                fontSize: 14,
                                width: 30,
                                height: 30,
                                bgcolor: assignee.profile_picture
                                  ? "transparent"
                                  : stringToColor(assignee.first_name),
                              }}
                            />
                          </Tooltip>
                        ))}
                        {subRow.assignees.length > 3 && (
                          <span className={styles.assigneeCount}>
                            +{subRow.assignees.length - 3}
                          </span>
                        )}
                      </div>
                    </div>
                    {/* <div className={`${styles.cell} ${styles.assignee}`}>
                                                {subRow.assignee
                                                    ? <Tooltip
                                                        title={`${subRow.assignee?.first_name} ${subRow.assignee?.last_name}`}
                                                        arrow placement="top">
                                                        <Avatar
                                                            alt={`${subRow.assignee?.first_name} ${subRow.assignee?.last_name}`}
                                                            src={" "}
                                                            sx={
                                                                {
                                                                    fontSize: 14,
                                                                    width: 30,
                                                                    height: 30,
                                                                    bgcolor: subRow.assignee.profile_picture ? 'transparent' : stringToColor(subRow.assignee.first_name),
                                                                }
                                                            }
                                                        />
                                                    </Tooltip>

                                                    : ''}
                                            </div> */}

                    {/* subtask priority */}
                    <div className={`${styles.cell} ${styles.priority}`}>
                      {subRow.priority ? subRow.priority : "-"}
                    </div>

                    {/* subtask progress */}
                    <div className={`${styles.cell} ${styles.progress}`}></div>

                    {/* subtask comments */}
                    <div className={`${styles.cell} ${styles.comments}`}>
                      <div
                        className={styles.iconLink}
                        onClick={() =>
                          toggleCommentSection(true, subRow.id, subRow.title)
                        }
                      >
                        {subRow.commentsCount}{" "}
                        <MdOpenInNew
                          style={{
                            color: "rgba(56, 56, 56, 0.4)",
                            fontSize: "15",
                          }}
                        />
                      </div>
                    </div>

                    {/* subtask due date */}
                    <div className={`${styles.cell} ${styles.dueDate}`}>
                      {formatDate(subRow.due_date)}
                    </div>

                    {/* subtask actions */}
                    <div className={`${styles.cell} ${styles.actions}`}>
                      <div className={styles.actionIcons}>
                        {subRow.is_completed && (
                          <Tooltip
                            title="Add a lesson learned."
                            arrow
                            placement="top"
                          >
                            <span>
                              <AddNewLessonIcon taskId={subRow?.id} />
                            </span>
                          </Tooltip>
                        )}
                        <ActionDropdown
                          is_sub_task={true}
                          taskId={row.id}
                          subtaskId={subRow.id}
                          loadAll={loadAllProjectTasks}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>

          {openTaskCreateUpdate && (
            <AddUpdateTask
              projectId={projectId}
              workStreamId={workStreamId}
              show={openTaskCreateUpdate}
              task={taskPopupOptions.task}
              setSubtask={setSubtask}
              loadAll={loadAllProjectTasks}
              is_new_task={taskPopupOptions.is_new}
              handleClose={() => setOpenTaskCreateUpdate(false)}
            />
          )}
          {openSubTaskCreateUpdate && (
            <AddUpdateSubTask
              projectId={projectId}
              workStreamId={workStreamId}
              task={taskPopupOptions.task}
              loadAll={loadAllProjectTasks}
              show={openSubTaskCreateUpdate}
              subTask={taskPopupOptions.subTask}
              is_new_task={taskPopupOptions.is_new}
              handleClose={() => setOpenSubTaskCreateUpdate(false)}
            />
          )}

          {/* generate task popup */}
          <ShowGeneratedTasks
            show={openGeneratedTasksPopup.show}
            handleClose={() => closeGeneratedTasksPopup()}
            goal={openGeneratedTasksPopup.goal}
            workStreamId={workStreamId}
            loadAllProjectTasks={loadAllProjectTasks}
          />
        </Box>
      </Box>

      {/* goal/task comments */}
      <Drawer
        anchor="right"
        open={openComments}
        onClose={toggleCommentSection}
        classes={{ paper: styles.drawerContent }}
      >
        <TaskComments
          close={toggleCommentSection}
          loadAll={loadAllProjectTasks}
          stringToColor={stringToColor}
          options={commentSectionOptions}
        />
      </Drawer>
    </div>
  );
};

export default TaskTable;

export const ActionDropdown = ({ is_sub_task, taskId, subtaskId, loadAll }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const deleteTaskOrSubTask = () => {
    if (is_sub_task) {
      deleteSubTask(taskId, subtaskId).then((res) => {
        if (res.status === 200) {
          showToast("Task is successfully deleted", "success");
          loadAll();
          handleClose();
        }
      });
    } else {
      deleteTask(taskId).then((res) => {
        if (res.status === 200) {
          showToast("Goal is successfully deleted", "success");
          loadAll();
          handleClose();
        }
      });
    }
  };

  return (
    <div>
      <Button
        id="basic-button"
        aria-controls={open ? "basic-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        onClick={handleClick}
        sx={{
          minWidth: 0,
          padding: 0,
          "& svg": {
            fontSize: "1.5rem",
            color: "#333",
          },
        }}
      >
        <IoEllipsisVertical color={"#777"} />
      </Button>
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "basic-button",
        }}
      >
        <MenuItem onClick={deleteTaskOrSubTask}>
          Delete {is_sub_task ? "Task" : "Goal"}
        </MenuItem>
      </Menu>
    </div>
  );
};

// AddNewLessonIcon component with AddNewLesson modal for adding new lessons
export const AddNewLessonIcon = ({ taskId }) => {
  const [open, setOpen] = useState(false);
  const handleClose = () => setOpen(false);

  return (
    <>
      <button onClick={() => setOpen(true)} className={styles.plusButton}>
        <BiSolidBookAdd size={14} />
      </button>
      <AddNewLesson handleClose={handleClose} show={open} taskId={taskId} />
    </>
  );
};
